import { useEffect, useState } from "react";

/**
 * Debounce a rapidly-changing value (a search box's text) so downstream effects
 * — the `?q=` people fetch, the global-search request — fire once per settled
 * query rather than once per keystroke. Returns the settled value.
 *
 * `delayMs <= 0` PASSES THROUGH in the same commit — no timer is armed. That is
 * a deliberate contract, not an optimisation: "debounce by zero" means "do not
 * debounce", and a `setTimeout(…, 0)` settle costs a controlled input one extra
 * render holding a stale value. It also makes a negative delay explicit instead
 * of leaving it to the timer spec's silent clamp-to-zero, and it keeps tests
 * deterministic without touching timers. Pinned in `useDebouncedValue.test.tsx`.
 *
 * ⚠️ THIS IS AN EXTRACT CANDIDATE, NOT A SEAM. Until 2026-07-30 the app shipped
 * this hook TWICE — `lib/useDebounce.ts` and `lib/useDebouncedValue.ts`, both
 * exporting this exact name, disagreeing on the zero-delay case, with
 * `Search.tsx` importing one and `People.tsx` the other. Both files claimed to
 * be "the seam where a `@norkron/web-*` timing utility plugs in later"; that
 * claim was FALSE and is why the drift went unnoticed. Measured 2026-07-30
 * across all 112 packages in `libs/frontend/web/`: NO package exports a
 * debounce or throttle primitive, and no source file is named for one. So there
 * is nothing to plug into — the honest disposition is to EXTRACT this upstream
 * (an FR is filed separately). Do not re-add a second copy in the meantime.
 */
export function useDebouncedValue<T>(value: T, delayMs = 250): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    if (delayMs <= 0) {
      setDebounced(value);
      return;
    }
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);

  return debounced;
}
